/**
 * Deterministic synthetic client business data for the deep-dive profile.
 * Seeded by a hash of the client id so each client always shows the same
 * plausible orders / quotes / POs. Replace these generators with live
 * Firestore reads (salesOrders, quotes, vendorPOs) when the backend is wired.
 */
export interface Order { id: string; po: string; product: string; qty: string; value: number; status: 'in_production' | 'ready' | 'shipped' | 'confirmed'; due: string; soId?: string; email?: string; rawStatus?: string; }
export interface Quote { id: string; product: string; qty: string; value: number; status: 'sent' | 'viewed' | 'negotiating' | 'draft'; age: string; osId?: string; }
export interface IncomingPO { id: string; po: string; received: string; value: number; ship: string; status: 'new' | 'reviewing' | 'accepted'; }
export interface Sku { sku: string; product: string; lastOrder: string; reorders: number; }

function seed(s: string): () => number {
  let h = 2166136261;
  for (const ch of s) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  return () => { h += 0x6D2B79F5; let t = h; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

const PRODUCTS = ['Stand-up pouch', 'Flat bottom pouch', 'Stick pack', 'Spouted pouch', 'Shrink sleeve', 'Roll stock film', 'Sachet', 'Gusseted bag', 'Lidding film', 'Printed label'];
const ORDER_ST: Order['status'][] = ['confirmed', 'in_production', 'ready', 'shipped'];
const QUOTE_ST: Quote['status'][] = ['draft', 'sent', 'viewed', 'negotiating'];
const PO_ST: IncomingPO['status'][] = ['new', 'reviewing', 'accepted'];
const pick = <T,>(r: () => number, a: T[]): T => a[Math.floor(r() * a.length)];
const money = (r: () => number, lo: number, hi: number) => Math.round((lo + r() * (hi - lo)) / 50) * 50;
const qty = (r: () => number) => `${(Math.floor(r() * 90) + 5)}K pcs`;

export function ordersFor(id: string): Order[] {
  const r = seed(id + 'orders');
  const n = Math.floor(r() * 4); // 0..3
  return Array.from({ length: n }, () => ({
    id: `SO-${1000 + Math.floor(r() * 8999)}`,
    po: `PO-${Math.floor(r() * 90000) + 10000}`,
    product: pick(r, PRODUCTS),
    qty: qty(r),
    value: money(r, 4000, 90000),
    status: pick(r, ORDER_ST),
    due: pick(r, ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']) + ' ' + (Math.floor(r() * 9) + 1) + ':00',
  }));
}

export function quotesFor(id: string): Quote[] {
  const r = seed(id + 'quotes');
  const n = Math.floor(r() * 3); // 0..2
  return Array.from({ length: n }, (_, i) => ({
    id: `Q-2026-${String(100 + (id.length * 11 + i * 17) % 899).padStart(3, '0')}`,
    product: pick(r, PRODUCTS),
    qty: qty(r),
    value: money(r, 3000, 70000),
    status: pick(r, QUOTE_ST),
    age: pick(r, ['2h', '1 day', '3 days', '1 week']) + ' ago',
  }));
}

export function posFor(id: string): IncomingPO[] {
  const r = seed(id + 'pos');
  const n = Math.floor(r() * 3); // 0..2
  return Array.from({ length: n }, (_, i) => ({
    id: `IPO-${i}`,
    po: `PO-${Math.floor(r() * 90000) + 10000}`,
    received: pick(r, ['Today', 'Yesterday', '2 days ago']),
    value: money(r, 5000, 80000),
    ship: pick(r, ['Net 30', 'Net 45', 'Net 60']),
    status: pick(r, PO_ST),
  }));
}

export function skusFor(id: string): Sku[] {
  const r = seed(id + 'skus');
  const n = Math.floor(r() * 4) + 1;
  return Array.from({ length: n }, (_, i) => ({
    sku: `SKU-${id.slice(5, 9).toUpperCase()}-${String(i + 1).padStart(3, '0')}`,
    product: pick(r, PRODUCTS),
    lastOrder: pick(r, ['12 days ago', '1 month ago', '6 weeks ago', '2 months ago']),
    reorders: Math.floor(r() * 8) + 1,
  }));
}

export const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** True only when the URL opts into synthetic demo data, e.g. ?demo=1 (also ?demo / ?demo=true).
 *  An absent param, ?demo=0, or ?demo=false are all treated as live (real data only).
 *  This is the single gate for every synthetic generator in this module. */
export function isDemoQuery(): boolean {
  if (typeof window === 'undefined') return false;
  const v = new URLSearchParams(window.location.search).get('demo');
  return v != null && v !== '0' && v !== 'false';
}

// ── sales-layer generators (deterministic per client) ────────
export type OppStage = 'inquiry' | 'qualified' | 'quoted' | 'negotiating' | 'won' | 'lost';
export interface Opp { id: string; title: string; stage: OppStage; value: number; owner: string; age: string; }
export interface DeepComm { id: string; channel: 'email' | 'phone' | 'text' | 'portal'; subject: string; preview: string; time: string; dir: 'in' | 'out'; }

const OWNERS = ['Fatima', 'Diego', 'Kim', 'Marisol'];
const OPP_TITLES = ['Stand-up pouch program', 'Reorder expansion', 'New SKU launch', 'Sample-to-production', 'Annual contract', 'Shrink sleeve trial'];
const STAGES: OppStage[] = ['inquiry', 'qualified', 'quoted', 'negotiating', 'won', 'lost'];

export function marginPct(id: string): number {
  const r = seed(id + 'margin'); return 0.22 + r() * 0.2;
}

export function oppsFor(id: string): Opp[] {
  const r = seed(id + 'opps');
  const n = Math.floor(r() * 2.4); // 0..2
  return Array.from({ length: n }, (_, i) => ({
    id: `OPP-2026-${String(100 + (id.length * 13 + i * 29) % 899).padStart(3, '0')}`,
    title: pick(r, OPP_TITLES),
    stage: pick(r, STAGES),
    value: money(r, 6000, 120000),
    owner: pick(r, OWNERS),
    age: pick(r, ['2 days', '1 week', '2 weeks', '1 month']),
  }));
}

export function commsFor(id: string): DeepComm[] {
  const r = seed(id + 'comms');
  const n = Math.floor(r() * 3.2); // 0..3
  const subjects = ['Quote follow-up', 'Artwork proof', 'Reorder inquiry', 'Sample request', 'Delivery timing', 'Invoice question', 'Pricing update'];
  const chans: DeepComm['channel'][] = ['email', 'phone', 'text', 'portal'];
  return Array.from({ length: n }, (_, i) => ({
    id: `${id}-cm-${i}`,
    channel: pick(r, chans),
    subject: pick(r, subjects),
    preview: 'Client message regarding open work and next steps.',
    time: pick(r, ['Today', 'Yesterday', '2 days ago', 'Last week']),
    dir: r() > 0.5 ? 'in' : 'out',
  }));
}

/** crude reorder signal from the most-stale SKU last order */
export function reorderSignal(id: string): { due: boolean; window: string; confidence: number; sku?: Sku } {
  const skus = skusFor(id);
  if (!skus.length) return { due: false, window: '—', confidence: 0 };
  const rank: Record<string, number> = { '12 days ago': 1, '1 month ago': 2, '6 weeks ago': 3, '2 months ago': 4 };
  const top = [...skus].sort((a, b) => (rank[b.lastOrder] || 0) - (rank[a.lastOrder] || 0))[0];
  const score = (rank[top.lastOrder] || 0) + Math.min(top.reorders, 5);
  return { due: score >= 5, window: score >= 7 ? 'Overdue' : score >= 5 ? 'This month' : 'Next quarter', confidence: Math.min(95, 45 + score * 7), sku: top };
}
